// Everything on campus, as a month — a card floating over the map, not a page
// replacing it, so the reader never loses where they are.
//
// The pins answer "what is on near me, soon" — they are capped at the coming
// week for exactly that reason. This answers the other question, the one a
// student asks while planning rather than while walking: what is on at all, and
// when. So it is a calendar and not a second list of pins.
//
// A day carrying events shows a count, and tapping it filters the list beneath
// to that day. Tapping it again clears the filter, because a calendar that can
// only ever be narrowed is a trap: there is no other way back to the month.
//
// Dates come in as `startsAt`, a millisecond timestamp the host computed from
// the event's own date and time — this file does no event-time arithmetic of
// its own, which is what keeps it from ever disagreeing with the pin beside it.
import { useMemo, useState } from 'react';

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** Midnight-anchored key for a day, so events group by calendar date. */
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

export default function EventCalendar({
  events,        // every upcoming event, in date order — see PathfinderTab
  onPick,        // (event) => open its sheet
  onClose,
  clubLogin = null,   // { href, label } — the host's club sign-in, top right
}) {
  const today = useMemo(() => new Date(), []);
  // Which month is on screen. Starts on the one containing the next event
  // rather than on this one: opening onto an empty January because the term
  // has not started looks like the calendar is broken.
  const [cursor, setCursor] = useState(() => {
    const first = events.find(e => e.startsAt);
    const d = first ? new Date(first.startsAt) : today;
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [picked, setPicked] = useState(null);   // a dayKey, or null for the month

  // dayKey → how many events start that day.
  const counts = useMemo(() => {
    const m = new Map();
    for (const e of events) {
      if (!e.startsAt) continue;
      const k = dayKey(new Date(e.startsAt));
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [events]);

  // Six weeks from the Sunday on or before the 1st — a fixed grid, so the
  // calendar does not change height as the months are paged through.
  const cells = useMemo(() => {
    const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      return { d, key: dayKey(d), inMonth: d.getMonth() === cursor.getMonth() };
    });
  }, [cursor]);

  // The list underneath: the picked day, or everything left in this month.
  const listed = useMemo(() => {
    if (picked) return events.filter(e => e.startsAt && dayKey(new Date(e.startsAt)) === picked);
    return events.filter(e => {
      if (!e.startsAt) return false;
      const d = new Date(e.startsAt);
      return d.getFullYear() === cursor.getFullYear() && d.getMonth() === cursor.getMonth();
    });
  }, [events, picked, cursor]);

  const monthName = cursor.toLocaleDateString([], { month: 'long', year: 'numeric' });
  const step = (n) => {
    setCursor(c => new Date(c.getFullYear(), c.getMonth() + n, 1));
    setPicked(null);   // a day in the month you just left is not a filter
  };

  return (
    <div className="pfa-cal">
      <div className="pfa-cal-head">
        <button className="pfa-cal-x" onClick={onClose} aria-label="Close">✕</button>
        <div>
          <strong>Events</strong>
          <span>{monthName}</span>
        </div>
        {clubLogin?.href && (
          <a className="pfa-cal-login" href={clubLogin.href}>{clubLogin.label || 'Club Login'}</a>
        )}
      </div>

      <div className="pfa-cal-body">
        <div className="pfa-cal-nav">
          <button onClick={() => step(-1)} aria-label="Previous month">‹</button>
          <b>{monthName}</b>
          <button onClick={() => step(1)} aria-label="Next month">›</button>
        </div>

        <div className="pfa-cal-grid">
          {DOW.map((d, i) => <div key={i} className="pfa-cal-dow">{d}</div>)}
          {cells.map(c => {
            const n = counts.get(c.key) || 0;
            const isToday = c.key === dayKey(today);
            return (
              <button
                key={c.key}
                className={`pfa-cal-day${c.inMonth ? '' : ' out'}${isToday ? ' today' : ''}${picked === c.key ? ' on' : ''}`}
                // A day with nothing on it is not a filter worth applying.
                disabled={!n}
                onClick={() => setPicked(p => (p === c.key ? null : c.key))}
              >
                <span>{c.d.getDate()}</span>
                {!!n && <i>{n}</i>}
              </button>
            );
          })}
        </div>

        <div className="pfa-cal-list">
          {picked && (
            <button className="pfa-cal-clear" onClick={() => setPicked(null)}>
              Showing one day — see all of {cursor.toLocaleDateString([], { month: 'long' })}
            </button>
          )}
          {!listed.length && (
            <div className="pfa-cal-empty">
              {picked ? 'Nothing on that day.' : `Nothing on in ${monthName}.`}
            </div>
          )}
          {listed.map(e => (
            <button key={e.id} className="pfa-cal-row" onClick={() => onPick(e)}>
              {e.logo
                ? <img src={e.logo} alt="" />
                : <div className="pfa-cal-rowblank" />}
              <div>
                <strong>{e.name}</strong>
                <span>{e.whenLong}</span>
                {/* `where` rather than the building alone: an off-campus event
                    has no building and would otherwise be the one row on this
                    list that says nothing at all about where it is. */}
                {(e.where || e.buildingName) && <i>{e.where || e.buildingName}</i>}
                {/* The tags belong in the list, not only in the sheet. This is
                    the screen somebody scans while deciding what to go to, and
                    "free food" decides it. */}
                {e.tags?.length > 0 && (
                  <div className="pfa-event-tags">
                    {/* By position, for the reason EventSheet's row says: one
                        key can cover several tags of the same kind. */}
                    {e.tags.map((t, i) => (
                      <span key={i} className={`pfa-event-tag pfa-event-tag-${t.key}`}
                            title={t.title || undefined}>{t.label}</span>
                    ))}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
